import {
  AdditiveBlending,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Points,
  ShaderMaterial,
} from "three";

// Procedural two-layer starfield rendered on a very large sphere.
// Layer A: many faint stars. Layer B: fewer bright stars with a slight
// warm/cool colour variation. Small custom shader gives each point a
// soft circular falloff instead of a hard square.

const STARFIELD_RADIUS = 90;
const FAINT_STAR_COUNT = 4000;
const MID_STAR_COUNT = 1400;
const BRIGHT_STAR_COUNT = 320;

export function createStars(): Points[] {
  return [
    buildLayer(FAINT_STAR_COUNT,  0.6, 1.3, 0.45),
    buildLayer(MID_STAR_COUNT,    1.0, 2.0, 0.8),
    buildLayer(BRIGHT_STAR_COUNT, 1.8, 3.4, 1.35),
  ];
}

function buildLayer(
  count: number,
  sizeMin: number,
  sizeMax: number,
  brightness: number,
): Points {
  const positions = new Float32Array(count * 3);
  const sizes = new Float32Array(count);
  const colors = new Float32Array(count * 3);

  const tmp = new Color();
  for (let i = 0; i < count; i++) {
    // Uniform random direction on a sphere
    const u = Math.random() * 2 - 1;
    const theta = Math.random() * Math.PI * 2;
    const r = Math.sqrt(1 - u * u);
    const x = r * Math.cos(theta);
    const y = r * Math.sin(theta);
    const z = u;

    positions[i * 3] = x * STARFIELD_RADIUS;
    positions[i * 3 + 1] = y * STARFIELD_RADIUS;
    positions[i * 3 + 2] = z * STARFIELD_RADIUS;

    // Most stars slightly blueish-white, with a few warm-tinted outliers.
    // Hue drawn from a narrow band around 220° with occasional 40° warmth.
    const warm = Math.random() < 0.15;
    const hue = warm ? 0.08 + Math.random() * 0.04 : 0.58 + Math.random() * 0.07;
    const sat = 0.15 + Math.random() * 0.25;
    const light = 0.7 + Math.random() * 0.25;
    tmp.setHSL(hue, sat, light);

    const intensity = brightness * (0.6 + Math.random() * 0.4);
    colors[i * 3] = tmp.r * intensity;
    colors[i * 3 + 1] = tmp.g * intensity;
    colors[i * 3 + 2] = tmp.b * intensity;

    sizes[i] = sizeMin + Math.random() * (sizeMax - sizeMin);
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setAttribute("aSize", new Float32BufferAttribute(sizes, 1));
  geo.setAttribute("aColor", new Float32BufferAttribute(colors, 3));

  const material = new ShaderMaterial({
    vertexShader: /* glsl */ `
      attribute float aSize;
      attribute vec3 aColor;
      varying vec3 vColor;
      uniform float uPixelRatio;
      void main() {
        vColor = aColor;
        vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPos;
        gl_PointSize = aSize * uPixelRatio * (220.0 / -mvPos.z);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying vec3 vColor;
      void main() {
        vec2 uv = gl_PointCoord - 0.5;
        float d = length(uv);
        if (d > 0.5) discard;
        // Soft circular falloff with a tiny core
        float core = smoothstep(0.5, 0.0, d);
        float glow = pow(core, 2.2);
        gl_FragColor = vec4(vColor * glow, glow);
      }
    `,
    uniforms: {
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
    },
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
  });

  const points = new Points(geo, material);
  points.frustumCulled = false;
  points.renderOrder = -1;
  return points;
}
